import React, { useState, useEffect } from 'react'
import { gql } from 'graphql-request'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { UserPlus, UserMinus, Search, Mail, Calendar, Plus, GraduationCap, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { OrganisationWithDetails, OrganisationUser } from '../index'

interface UsersTabProps {
  organisation: OrganisationWithDetails | null
  loading: boolean
  onRefresh: () => void
}

interface AvailableUser {
  id: number
  email: string
  firstName: string | null
  lastName: string | null
  isActive: boolean
  organisationId: number | null
}

const GET_ALL_USERS = gql`
  query getAllUsers {
    users: getUsers {
      id
      email
      firstName
      lastName
      isActive
      organisationId
    }
  }
`

const ATTACH_USER = gql`
  mutation attachUserToOrganisation($input: AttachUserInput!) {
    attachUserToOrganisation(input: $input) {
      id
      email
      firstName
      lastName
      isCoach
    }
  }
`

const DETACH_USER = gql`
  mutation detachUserFromOrganisation($userId: Int!) {
    detachUserFromOrganisation(userId: $userId) {
      id
      email
      firstName
      lastName
    }
  }
`

const CREATE_USER = gql`
  mutation createUser($input: CreateUserInput!) {
    createUser(input: $input) {
      id
      email
      firstName
      lastName
      isActive
      isCoach
    }
  }
`

interface B2cUserMinutes {
  userId: number
  subscriptionStatus: string | null
  planName: string | null
  totalSeconds: number
}

const GET_B2C_USER_MINUTES = gql`
  query getB2cUserMinutes($organisationId: Int!) {
    getB2cUserMinutes(organisationId: $organisationId) {
      userId
      subscriptionStatus
      planName
      totalSeconds
    }
  }
`

const SUBSCRIPTION_BADGE: Record<string, string> = {
  active: 'bg-green-50 text-green-700',
  past_due: 'bg-amber-50 text-amber-700',
  unpaid: 'bg-red-50 text-red-700',
  canceled: 'bg-gray-100 text-gray-500',
}

const UPDATE_COACH_STATUS = gql`
  mutation updateUserCoachStatus($userId: Int!, $isCoach: Boolean!) {
    updateUserCoachStatus(userId: $userId, isCoach: $isCoach) {
      id
      isCoach
    }
  }
`

const UsersTab: React.FC<UsersTabProps> = ({ organisation, loading, onRefresh }) => {
  const [showAddUser, setShowAddUser] = useState(false)
  const [showCreateUser, setShowCreateUser] = useState(false)
  const [availableUsers, setAvailableUsers] = useState<AvailableUser[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // Create user form state
  const [newUserEmail, setNewUserEmail] = useState('')
  const [newUserFirstName, setNewUserFirstName] = useState('')
  const [newUserLastName, setNewUserLastName] = useState('')
  const [createError, setCreateError] = useState<string | null>(null)
  // Set when Create User was refused because the address already has an account that
  // belongs to no organisation: the admin wanted Attach, so it is offered in place.
  const [duplicateUser, setDuplicateUser] = useState<AvailableUser | null>(null)

  // Members enter a B2C organisation through join links only; the one person an
  // administrator creates or attaches by hand there is a coach, who needs no
  // subscription (spec coach-role). The same two panels serve both kinds.
  const isB2c = organisation?.kind === 'b2c'
  const [minutes, setMinutes] = useState<B2cUserMinutes[]>([])

  useEffect(() => {
    if (!isB2c || !organisation) {
      setMinutes([])
      return
    }
    let cancelled = false
    authorizedGraphQLRequest<{ getB2cUserMinutes: B2cUserMinutes[] }>(GET_B2C_USER_MINUTES, { organisationId: organisation.id })
      .then((response) => {
        if (!cancelled) setMinutes(response.getB2cUserMinutes || [])
      })
      .catch((error) => console.error('Failed to fetch user balances:', error))
    return () => {
      cancelled = true
    }
  }, [isB2c, organisation])

  // Fetch available users (users not assigned to any organisation)
  useEffect(() => {
    if (showAddUser) {
      fetchAvailableUsers()
    }
  }, [showAddUser])

  const fetchAvailableUsers = async () => {
    try {
      setLoadingUsers(true)
      const response = await authorizedGraphQLRequest<{ users: AvailableUser[] }>(GET_ALL_USERS)
      // Filter users that don't have an organisation
      const unassigned = (response.users || []).filter((user) => !user.organisationId)
      setAvailableUsers(unassigned)
    } catch (error) {
      console.error('Failed to fetch users:', error)
    } finally {
      setLoadingUsers(false)
    }
  }

  const handleAttachUser = async (userId: number): Promise<boolean> => {
    if (!organisation) return false

    try {
      setSubmitting(true)
      await authorizedGraphQLRequest(ATTACH_USER, {
        input: {
          userId,
          organisationId: organisation.id,
          // In a B2C organisation the only person attached by hand is a coach.
          ...(isB2c ? { isCoach: true } : {}),
        },
      })
      setShowAddUser(false)
      setSearchTerm('')
      onRefresh()
      return true
    } catch (error) {
      console.error('Failed to attach user:', error)
      alert('Failed to attach user to organisation')
      return false
    } finally {
      setSubmitting(false)
    }
  }

  const handleDetachUser = async (user: OrganisationUser) => {
    if (!organisation) return
    // Named, because the icon sits in every row and a detached account keeps nothing
    // that says where it came from. A B2C member can only come back through a join link.
    const consequence = isB2c ? 'They will only be able to come back through a join link.' : 'They can be attached again from the Attach User panel.'
    if (!confirm(`Remove ${user.email} from ${organisation.name}?\n\n${consequence}`)) return

    try {
      await authorizedGraphQLRequest(DETACH_USER, { userId: user.id })
      onRefresh()
    } catch (error) {
      console.error('Failed to detach user:', error)
      alert('Failed to remove user from organisation')
    }
  }

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!organisation) return
    if (!newUserEmail.trim()) {
      setCreateError('Email is required')
      return
    }

    const email = newUserEmail.trim()

    try {
      setSubmitting(true)
      setCreateError(null)
      setDuplicateUser(null)
      await authorizedGraphQLRequest(CREATE_USER, {
        input: {
          email,
          firstName: newUserFirstName.trim() || null,
          lastName: newUserLastName.trim() || null,
          organisationId: organisation.id,
          // In a B2C organisation the only person created by hand is a coach.
          ...(isB2c ? { isCoach: true } : {}),
        },
      })
      resetCreateForm()
      onRefresh()
    } catch (error: any) {
      console.error('Failed to create user:', error)
      const message: string = error?.response?.errors?.[0]?.message || 'Failed to create user'
      if (/already exists/i.test(message)) {
        setCreateError(await describeExistingUser(email))
      } else {
        setCreateError(message)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const resetCreateForm = () => {
    setNewUserEmail('')
    setNewUserFirstName('')
    setNewUserLastName('')
    setCreateError(null)
    setDuplicateUser(null)
    setShowCreateUser(false)
  }

  // "Already exists" on its own dead-ends the admin: the account they typed is usually
  // one that was removed from an organisation, and Attach User is the way back. Says
  // which case it is, and sets `duplicateUser` when attaching is the answer.
  const describeExistingUser = async (email: string): Promise<string> => {
    try {
      const response = await authorizedGraphQLRequest<{ users: AvailableUser[] }>(GET_ALL_USERS)
      const existing = (response.users || []).find((user) => user.email.toLowerCase() === email.toLowerCase())
      if (existing && !existing.organisationId) {
        setDuplicateUser(existing)
        return 'A user with this email already exists but belongs to no organisation.'
      }
      if (existing && existing.organisationId === organisation?.id) {
        return 'A user with this email is already in this organisation.'
      }
      if (existing) {
        return 'A user with this email already exists in another organisation. Remove them there before adding them here.'
      }
    } catch (error) {
      console.error('Failed to look up existing user:', error)
    }
    return 'User with this email already exists'
  }

  const handleAttachDuplicate = async () => {
    if (!duplicateUser) return
    if (await handleAttachUser(duplicateUser.id)) resetCreateForm()
  }

  const handleToggleCoach = async (userId: number, currentStatus: boolean) => {
    try {
      await authorizedGraphQLRequest(UPDATE_COACH_STATUS, {
        userId,
        isCoach: !currentStatus,
      })
      onRefresh()
    } catch (error) {
      console.error('Failed to update coach status:', error)
    }
  }

  const filteredUsers = availableUsers.filter(
    (user) =>
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.firstName && user.firstName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.lastName && user.lastName.toLowerCase().includes(searchTerm.toLowerCase())),
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!organisation) {
    return <div className="text-center text-gray-500 py-12">No organisation selected</div>
  }

  return (
    <div className="space-y-6">
      {isB2c && (
        <p className="text-sm text-gray-500">
          Members join this organisation through its join links; each shows their subscription and total minutes left. Coaches are added here and need no subscription.
        </p>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => {
            setShowCreateUser(!showCreateUser)
            setShowAddUser(false)
          }}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          {isB2c ? 'Create coach' : 'Create User'}
        </Button>
        <Button
          onClick={() => {
            setShowAddUser(!showAddUser)
            setShowCreateUser(false)
          }}
          className="flex items-center gap-2"
        >
          <UserPlus className="h-4 w-4" />
          {isB2c ? 'Attach coach' : 'Attach User'}
        </Button>
      </div>

      {/* Create User Form */}
      {showCreateUser && (
        <div className="bg-gray-50 rounded-xl p-6">
          <h4 className="font-semibold text-gray-800 mb-4">{isB2c ? 'Create coach' : 'Create New User'}</h4>
          <form onSubmit={handleCreateUser} className="space-y-4">
            {createError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center justify-between gap-4">
                <span>{createError}</span>
                {duplicateUser && (
                  <Button type="button" size="sm" onClick={handleAttachDuplicate} disabled={submitting} className="shrink-0">
                    <UserPlus className="h-4 w-4 mr-1" />
                    Attach to {organisation.name}
                  </Button>
                )}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                placeholder="user@example.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  value={newUserFirstName}
                  onChange={(e) => setNewUserFirstName(e.target.value)}
                  placeholder="John"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  value={newUserLastName}
                  onChange={(e) => setNewUserLastName(e.target.value)}
                  placeholder="Doe"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
            <p className="text-sm text-gray-500">
              The user will be automatically added to <strong>{organisation.name}</strong>
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowCreateUser(false)
                  setCreateError(null)
                  setDuplicateUser(null)
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Creating...' : isB2c ? 'Create coach' : 'Create User'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Add User Panel */}
      {showAddUser && (
        <div className="bg-gray-50 rounded-xl p-6 space-y-4">
          <h4 className="font-semibold text-gray-800 mb-4">{isB2c ? 'Attach a coach' : 'Attach User to Organisation'}</h4>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* Available Users List */}
          <div className="max-h-64 overflow-y-auto space-y-2">
            {loadingUsers ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center text-gray-500 py-4">{searchTerm ? 'No users found matching your search' : 'No unassigned users available'}</div>
            ) : (
              filteredUsers.map((user) => (
                <div key={user.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-primary font-semibold">{(user.firstName?.[0] || user.email[0]).toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email.split('@')[0]}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                  </div>
                  <Button size="sm" onClick={() => handleAttachUser(user.id)} disabled={submitting}>
                    <UserPlus className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                </div>
              ))
            )}
          </div>

          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={() => setShowAddUser(false)}>
              Close
            </Button>
          </div>
        </div>
      )}

      {/* Current Users List */}
      <div className="space-y-4">
        <h4 className="font-semibold text-gray-800">Organisation Users ({organisation.users.length})</h4>

        {organisation.users.length === 0 ? (
          <div className="text-center text-gray-500 py-8 bg-gray-50 rounded-xl">
            <p>No users in this organisation</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {organisation.users.map((user: OrganisationUser) => (
              <div key={user.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary to-primary/70 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-lg">{(user.firstName?.[0] || user.email[0]).toUpperCase()}</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">{user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email.split('@')[0]}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {user.email}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(user.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    {isB2c &&
                      (() => {
                        const row = minutes.find((m) => m.userId === user.id)
                        const status = row?.subscriptionStatus
                        return (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${status ? (SUBSCRIPTION_BADGE[status] ?? 'bg-gray-100 text-gray-600') : 'bg-gray-100 text-gray-500'}`}
                            >
                              {status ? `${row?.planName ?? 'Plan'}: ${status.replace('_', ' ')}` : 'No subscription'}
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700">
                              <Clock className="h-3 w-3" />
                              {Math.round((row?.totalSeconds ?? 0) / 60)} min
                            </span>
                          </div>
                        )
                      })()}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {/* Any member can be a coach, whatever the organisation's kind. A B2C
                      coach is exempt from the subscription gate (spec coach-role). */}
                  <button
                    onClick={() => handleToggleCoach(user.id, !!user.isCoach)}
                    className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer ${user.isCoach ? 'bg-purple-100 text-purple-800 hover:bg-purple-200' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                    title={user.isCoach ? 'Remove coach role' : 'Make coach'}
                  >
                    <GraduationCap className="h-3 w-3" />
                    Coach
                  </button>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {user.isActive ? 'Active' : 'Inactive'}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    title={`Remove ${user.email} from the organisation`}
                    aria-label={`Remove ${user.email} from the organisation`}
                    onClick={() => handleDetachUser(user)}
                  >
                    <UserMinus className="h-4 w-4 mr-1" />
                    Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default UsersTab
